<section class="relative h-[85vh] md:h-screen w-full">
	<!-- Video Container -->
	<div class="relative w-full h-full overflow-hidden">
		<!-- Video Background -->
		<video
			#heroVideo
			class="absolute inset-0 w-full h-full object-cover z-0 scale-105"
			autoplay
			muted
			loop
			playsinline
			webkit-playsinline
			preload="auto">
			<source src="/videos/bg.mp4" type="video/mp4">
		</video>

		<!-- Premium Gradient Overlay -->
		<div class="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-primary/30 z-5"></div>

		<!-- Animated Mesh Gradient Overlay -->
		<div class="absolute inset-0 z-6 opacity-20">
			<div class="absolute top-0 -left-1/4 w-96 h-96 bg-primary rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
			<div class="absolute bottom-0 -right-1/4 w-96 h-96 bg-secondary rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style="animation-delay: 1s;"></div>
		</div>

		<!-- Content Container -->
		<div class="relative z-10 h-full flex flex-col max-w-[1400px] mx-auto px-6 md:px-8">
			<!-- Main Content Area - Positioned at Bottom Left -->
			<div class="flex-1 flex items-end pb-12 md:pb-16 lg:pb-20">
				<div class="w-full max-w-2xl">
					<!-- Main Heading -->
					<h1 class="font-cenzo text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-6">
						<span class="text-mist block mb-2">{{ heroTitle1() }}</span>
						<span class="bg-gradient-to-r from-secondary via-white to-secondary bg-clip-text text-transparent animate-shimmer bg-[length:200%_100%] text-nowrap">
							{{ heroTitle2() }}
						</span>
					</h1>

					<!-- Description -->
					<p class="text-base md:text-lg lg:text-xl text-mist/90 leading-relaxed mb-8">
						{{ heroDescription() }}
					</p>

					<!-- CTA Buttons -->
					<div class="flex flex-wrap gap-4 md:gap-6">
						<a [href]="'tel:' + heroPhone()" class="group relative overflow-hidden btn-slide-border flex items-center gap-2 border-2 border-mist text-mist px-6 py-3.5 rounded-full font-semibold text-sm md:text-base tracking-wide shadow-lg hover:shadow-2xl hover:shadow-primary/20 transition-all duration-300">
							<div class="relative z-10 flex items-center gap-2">
								<span>Call Now</span>
							</div>
						</a>

						<button (click)="openBookingModal()" class="group relative overflow-hidden bg-primary text-white px-6 py-3.5 rounded-full font-bold text-sm md:text-base tracking-wide shadow-lg hover:shadow-2xl hover:shadow-secondary/30 transition-all duration-300 hover:scale-105">
							<div class="flex items-center gap-2">
								<span>Book Appointment</span>
							</div>
						</button>
					</div>
				</div>
			</div>

			<!-- Video Control Button - Bottom Right -->
			<div class="absolute bottom-6 right-6 md:bottom-8 md:right-8 z-20">
				<button
					(click)="toggleVideo()"
					class="group w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-white/20 hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl">
					<svg *ngIf="isPlaying" class="w-5 h-5 md:w-6 md:h-6 text-mist" fill="currentColor" viewBox="0 0 24 24">
						<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
					</svg>
					<svg *ngIf="!isPlaying" class="w-5 h-5 md:w-6 md:h-6 text-mist ml-0.5" fill="currentColor" viewBox="0 0 24 24">
						<path d="M8 5v14l11-7z"/>
					</svg>
				</button>
			</div>
		</div>
	</div>
</section>

<!-- Services Section -->
<section class="relative py-20 md:py-28 lg:py-32 px-8 md:px-16 lg:px-20 overflow-hidden">
	<!-- Background Pattern -->
	<div class="absolute inset-0 bg-mist"></div>
	<div class="absolute inset-0 opacity-5" style="background-image: radial-gradient(circle at 1px 1px, #5d202d 1px, transparent 0); background-size: 40px 40px;"></div>

	<div class="relative max-w-[1400px] mx-auto">
		<!-- Section Title with Enhanced Design -->
		<div class="text-center mb-16 md:mb-20 lg:mb-24">
			<div class="inline-block relative">
				<!-- Decorative Elements -->
				<div class="absolute -top-4 -left-4 w-10 h-10 border-l-2 border-t-2 border-secondary"></div>
				<div class="absolute -bottom-4 -right-4 w-10 h-10 border-r-2 border-b-2 border-secondary"></div>

				<h2 class="font-cenzo text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 tracking-tight relative px-8">
					{{ servicesTitle() }}
					<!-- Multi-layer underline -->
					<span class="absolute -bottom-3 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-secondary to-transparent opacity-60"></span>
					<span class="absolute -bottom-4 left-1/4 right-1/4 h-0.5 bg-primary"></span>
					<span class="absolute -bottom-5 left-1/3 right-1/3 h-px bg-secondary"></span>
				</h2>
			</div>
			<p class="text-charcoal/70 text-base md:text-lg mt-8 max-w-2xl mx-auto leading-relaxed">
				{{ servicesDescription() }}
			</p>
		</div>

		<!-- Service Cards with Advanced Layout -->
		@if (servicesLoading()) {
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12 auto-rows-fr">
			@for (i of [1,2,3]; track i) {
			<div class="min-h-[320px] rounded-2xl bg-charcoal/5 animate-pulse"></div>
			}
		</div>
		} @else if (featuredServices().length === 0) {
		<div class="text-center py-16">
			<p class="text-charcoal/50 text-lg">Services coming soon...</p>
		</div>
		} @else {
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12 auto-rows-fr">
			@for (service of featuredServices(); track service.id; let idx = $index) {
				<div class="service-card group relative min-h-[320px] md:min-h-[320px] flex flex-col rounded-2xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-700 ease-out cursor-pointer transform hover:-translate-y-2">
					<!-- Card Border Glow Effect -->
					<div class="absolute inset-0 bg-gradient-to-br from-secondary/20 via-transparent to-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-2xl"></div>

					<!-- Image Container with Advanced Effects -->
					<div class="absolute inset-0 overflow-hidden">
						<img [src]="getServiceImageUrl(service)" [alt]="service.service_name" class="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-125 group-hover:rotate-2">
						<!-- Multi-layer overlays -->
						<div class="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-700"></div>
						<div class="absolute inset-0 bg-gradient-to-br from-primary/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
					</div>

					<!-- Number Badge -->
					<div class="absolute top-6 right-6 w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:bg-secondary/20">
						<span class="text-2xl font-bold text-white">{{ (idx + 1) < 10 ? '0' + (idx + 1) : idx + 1 }}</span>
					</div>

					<!-- Default State - Bottom aligned (Desktop Only) -->
					<div class="hidden md:block absolute bottom-0 left-0 right-0 p-8 transition-all duration-700 group-hover:opacity-0 group-hover:translate-y-4">
						<div class="flex items-center gap-3 mb-3">
							<div class="w-12 h-0.5 bg-secondary"></div>
							<span class="text-secondary text-sm font-semibold tracking-wider">{{ service.tagline || 'PREMIUM' }}</span>
						</div>
						<h3 class="font-cenzo text-2xl md:text-3xl font-bold text-white leading-tight">{{ service.service_name }}</h3>
					</div>

				<!-- Mobile Backdrop Overlay (Always visible on mobile) -->
				<div class="absolute inset-0 bg-black/70 md:opacity-0 md:group-hover:bg-black/70 transition-opacity duration-700"></div>

				<!-- Hover State - Full Card Content (Always visible on mobile, hover on desktop) -->
					<div class="absolute inset-0 p-8 flex flex-col justify-between opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-700 transform translate-y-0 md:translate-y-8 md:group-hover:translate-y-0">
						<!-- Top Content -->
						<div>
							<div class="flex items-center gap-3 mb-4">
								<div class="w-16 h-0.5 bg-secondary transition-all duration-700"></div>
								<span class="text-secondary text-sm font-semibold tracking-widest">PREMIUM SERVICE</span>
							</div>
							<h3 class="font-cenzo text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">{{ service.service_name }}</h3>
						</div>

						<!-- Bottom Content -->
						<div class="space-y-6">
							<p class="text-white/90 text-base leading-relaxed">
								{{ service.short_description || service.description }}
							</p>

							<!-- Details -->
							<div class="space-y-2">
								<div class="flex items-center gap-2 text-white/80 text-sm">
									<svg class="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
									</svg>
									<span>{{ service.duration_formatted || service.duration_minutes + ' mins' }}</span>
								</div>
								@if (service.pricing > 0) {
								<div class="flex items-center gap-2 text-white/80 text-sm">
									<svg class="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
									</svg>
									<span>Starting from ₹{{ service.pricing }}</span>
								</div>
								}
							</div>

						<button (click)="openBookingModal()" class="group/btn relative w-full border-2 border-white/60 text-white px-8 py-3.5 rounded-full font-semibold text-xs tracking-wider overflow-hidden flex items-center justify-center gap-3 transition-all duration-500 hover:bg-white/20 hover:backdrop-blur-xl hover:border-white hover:shadow-xl hover:shadow-white/20">
							<span class="relative z-10 transition-all duration-300 group-hover/btn:tracking-widest">BOOK CONSULTATION</span>
						</button>
						</div>
					</div>
				</div>
			}
		</div>
		}

		<!-- View All Services Button -->
		<div class="text-center mt-12 md:mt-16">
			<a routerLink="/services" class="inline-flex items-center gap-3 border-2 border-primary text-primary px-8 py-4 rounded-full font-bold text-sm tracking-wider transition-all duration-500 hover:bg-primary hover:text-white hover:shadow-xl hover:shadow-primary/20">
				<span>View All Services</span>
			</a>
		</div>
	</div>
</section>

<!-- Consultation CTA Section -->
<section class="relative py-24 md:py-32 lg:py-40 px-8 md:px-16 lg:px-20 overflow-hidden">
	<!-- Animated Background -->
	<div class="absolute inset-0 bg-gradient-to-br from-charcoal via-primary to-charcoal"></div>
	<div class="absolute inset-0 opacity-10" style="background-image: repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.03) 35px, rgba(255,255,255,.03) 70px);"></div>

	<!-- Animated Geometric Shapes -->
	<div class="absolute top-20 left-10 w-72 h-72 bg-secondary/10 rounded-full blur-3xl animate-pulse"></div>
	<div class="absolute bottom-20 right-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse" style="animation-delay: 1s;"></div>

	<!-- Decorative Lines -->
	<div class="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/50 to-transparent"></div>
	<div class="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/50 to-transparent"></div>

	<div class="relative max-w-[1400px] mx-auto">
		<div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
			<!-- Left Content -->
			<div class="space-y-8 lg:pr-8">
				<!-- Tagline -->
				<div class="flex items-center gap-4">
					<div class="w-16 h-px bg-secondary"></div>
					<span class="text-secondary text-sm md:text-base font-semibold tracking-[0.3em] uppercase">{{ ctaTagline() }}</span>
				</div>

				<!-- Main Heading -->
				<div class="space-y-6">
					<h2 class="font-cenzo text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1]">
						{{ ctaTitleLine1() }}
						<span class="block mt-2 bg-gradient-to-r from-white via-secondary to-white bg-clip-text text-transparent italic font-serif">
							{{ ctaTitleLine2() }}
						</span>
					</h2>

					<!-- Decorative Underline -->
					<div class="flex items-center gap-3">
						<div class="w-24 h-1 bg-secondary rounded-full"></div>
						<div class="w-16 h-1 bg-secondary/50 rounded-full"></div>
						<div class="w-8 h-1 bg-secondary/30 rounded-full"></div>
					</div>
				</div>

				<!-- Description -->
				<p class="text-white/80 text-base md:text-lg lg:text-xl leading-relaxed max-w-xl">
					{{ ctaDescription() }}
				</p>
			</div>

			<!-- Right CTA Section -->
			<div class="relative flex flex-col justify-center space-y-5">
				<!-- Main CTA Button -->
				<button (click)="openBookingModal()" class="btn-slide-border-white w-full bg-transparent border-2 border-white text-white px-12 py-7 rounded-2xl font-bold text-xl tracking-wider cursor-pointer">
					<div class="flex items-center justify-center gap-4">
						<svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
						</svg>
						<span>BOOK CONSULTATION</span>
					</div>
				</button>

				<!-- Divider with Text -->
				<div class="flex items-center gap-4">
					<div class="flex-1 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
					<span class="text-white/50 text-sm font-medium tracking-wider">OR</span>
					<div class="flex-1 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
				</div>

				<!-- Icon Links -->
				<div class="flex items-center justify-center gap-6">
					<a href="tel:+919876543210" class="group w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-white/20 hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl" title="Call us anytime">
						<svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
						</svg>
					</a>

					<a href="#map-div" class="group w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-white/20 hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl" title="Visit our studio map">
						<svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
						</svg>
					</a>
				</div>
			</div>
		</div>
	</div>
</section>
<!-- Blog Section -->
<section class="relative py-20 md:py-28 lg:py-32 px-8 md:px-16 lg:px-20 bg-white overflow-hidden">
	<!-- Background Elements -->
	<div class="absolute inset-0 opacity-5">
		<div class="absolute top-20 right-10 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
		<div class="absolute bottom-20 left-10 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
	</div>

	<div class="relative max-w-[1400px] mx-auto">
		<!-- Section Header -->
		<div class="text-center mb-16 md:mb-20">
			<div class="inline-flex items-center gap-3 mb-4">
				<div class="h-px w-12 bg-gradient-to-r from-transparent to-secondary"></div>
				<span class="text-secondary text-sm font-semibold tracking-widest uppercase">{{ blogTagline() }}</span>
				<div class="h-px w-12 bg-gradient-to-l from-transparent to-secondary"></div>
			</div>
			<h2 class="font-cenzo text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 tracking-tight">
				{{ blogTitle() }}
			</h2>
			<p class="text-charcoal/70 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
				{{ blogDescription() }}
			</p>
		</div>

		<!-- Blog Grid -->
		@if (blogsLoading()) {
			<!-- Loading State -->
			<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
				@for (i of [1,2,3]; track i) {
					<div class="bg-mist/30 rounded-2xl overflow-hidden shadow-lg animate-pulse">
						<div class="h-64 bg-charcoal/10"></div>
						<div class="p-6 space-y-4">
							<div class="h-4 bg-charcoal/10 rounded w-3/4"></div>
							<div class="h-4 bg-charcoal/10 rounded w-1/2"></div>
							<div class="h-20 bg-charcoal/10 rounded"></div>
						</div>
					</div>
				}
			</div>
		} @else if (latestBlogs().length > 0) {
			<!-- Blog Cards -->
			<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
				@for (blog of latestBlogs(); track blog.id) {
					<a
					[routerLink]="['/blog', blog.slug]"
					class="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-charcoal/5 hover:-translate-y-2">

						<!-- Featured Image -->
						<div class="relative h-64 overflow-hidden bg-gradient-to-br from-primary/10 to-secondary/10">
							<img
								[src]="getImageUrl(blog.featured_image_url)"
								[alt]="blog.title"
								class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
							/>
							<!-- Gradient Overlay -->
							<div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

							<!-- Reading Time Badge -->
							<div class="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm border border-white/20 flex items-center gap-2">
								<svg class="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
								</svg>
								<span class="text-xs font-semibold text-charcoal">{{ blog.reading_time_minutes }} min read</span>
							</div>
						</div>

						<!-- Content -->
						<div class="p-6">
							<!-- Date & Author -->
							<div class="flex items-center gap-3 text-xs text-charcoal/50 mb-3">
								<div class="flex items-center gap-1">
									<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
									</svg>
									<span>{{ formatDate(blog.created_at) }}</span>
								</div>
								@if (blog.user) {
									<span class="text-charcoal/30">•</span>
									<span>{{ blog.user.name }}</span>
								}
							</div>

							<!-- Title -->
							<h3 class="font-cenzo text-xl md:text-2xl font-bold text-primary mb-3 line-clamp-2 group-hover:text-secondary transition-colors duration-300">
								{{ blog.title }}
							</h3>

							<!-- Excerpt -->
							<p class="text-charcoal/70 text-sm leading-relaxed line-clamp-3 mb-4">
								{{ blog.short_description }}
							</p>

							<!-- Read More Link -->
							<div class="flex items-center gap-2 text-secondary font-semibold text-sm group-hover:gap-3 transition-all duration-300">
								<span>Read Article</span>
							</div>
						</div>

						<!-- Decorative Border Effect -->
						<div class="absolute inset-0 border-2 border-transparent group-hover:border-secondary/20 rounded-2xl transition-all duration-500 pointer-events-none"></div>
					</a>
				}
			</div>

			<!-- View All Button -->
			<div class="text-center mt-12 md:mt-16">
				<a
					routerLink="/blog"
					class="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
					<span>Explore All Articles</span>
				</a>
			</div>
		} @else {
			<!-- Empty State -->
			<div class="text-center py-12">
				<div class="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
					<svg class="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
					</svg>
				</div>
				<h3 class="font-cenzo text-2xl font-bold text-primary mb-2">No Articles Yet</h3>
				<p class="text-charcoal/60">Check back soon for expert insights and tips</p>
			</div>
		}
	</div>
</section>
<!-- Testimonials Section -->
<!-- <section class="relative py-20 md:py-28 lg:py-32 overflow-hidden bg-primary/5">
	<div class="text-center mb-12 md:mb-16 px-4 md:px-8">
		<h2 class="font-cenzo text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4 tracking-tight">
			What Our Clients Say
		</h2>
		<p class="text-charcoal/60 text-sm md:text-base mt-4 max-w-xl mx-auto">
			Real experiences from people who transformed their confidence
		</p>
	</div>

	<div class="max-w-7xl mx-auto px-4 md:px-8">
		<div class="md:hidden">
			<div class="testimonial-column">
				<div class="testimonial-track">
					@for (testimonial of mobileTestimonials; track testimonial.name) {
						<div class="testimonial-item relative bg-white/80 backdrop-blur-lg rounded-xl p-5 border border-white/20 shadow-lg hover:shadow-xl transition-shadow duration-300">
							<div class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
								<svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
									<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
									<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
									<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
									<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
								</svg>
							</div>
							<div class="flex gap-1 mb-3">
								@for (star of [1,2,3,4,5]; track star) {
									<svg class="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
									</svg>
								}
							</div>
							<p class="text-charcoal/80 text-base leading-relaxed mb-4">{{ testimonial.content }}</p>
							<div class="flex items-center gap-3">
								<div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
									<span class="text-primary font-semibold">{{ testimonial.name.charAt(0) }}{{ testimonial.name.split(' ')[1]?.charAt(0) }}</span>
								</div>
								<div>
									<p class="font-semibold text-primary">{{ testimonial.name }}</p>
									<p class="text-xs text-charcoal/50">{{ testimonial.role }}</p>
								</div>
							</div>
						</div>
					}

					@for (testimonial of mobileTestimonials; track testimonial.name + '_duplicate') {
						<div class="testimonial-item relative bg-white/80 backdrop-blur-lg rounded-xl p-5 border border-white/20 shadow-lg hover:shadow-xl transition-shadow duration-300">
							<div class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
								<svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
									<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
									<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
									<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
									<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
								</svg>
							</div>
							<div class="flex gap-1 mb-3">
								@for (star of [1,2,3,4,5]; track star) {
									<svg class="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
									</svg>
								}
							</div>
							<p class="text-charcoal/80 text-base leading-relaxed mb-4">{{ testimonial.content }}</p>
							<div class="flex items-center gap-3">
								<div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
									<span class="text-primary font-semibold">{{ testimonial.name.charAt(0) }}{{ testimonial.name.split(' ')[1]?.charAt(0) }}</span>
								</div>
								<div>
									<p class="font-semibold text-primary">{{ testimonial.name }}</p>
									<p class="text-xs text-charcoal/50">{{ testimonial.role }}</p>
								</div>
							</div>
						</div>
					}
				</div>
			</div>
		</div>

		<div class="hidden md:grid grid-cols-3 gap-4 md:gap-6">

			<div class="testimonial-column">
				<div class="testimonial-track">
					@for (testimonial of column1; track testimonial.name) {
						<div class="testimonial-item relative bg-white/80 backdrop-blur-lg rounded-xl p-5 border border-white/20 shadow-lg hover:shadow-xl transition-shadow duration-300">
							<div class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
								<svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
									<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
									<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
									<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
									<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
								</svg>
							</div>
							<div class="flex gap-1 mb-3">
								@for (star of [1,2,3,4,5]; track star) {
									<svg class="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
									</svg>
								}
							</div>
							<p class="text-charcoal/80 text-base leading-relaxed mb-4">{{ testimonial.content }}</p>
							<div class="flex items-center gap-3">
								<div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
									<span class="text-primary font-semibold">{{ testimonial.name.charAt(0) }}{{ testimonial.name.split(' ')[1]?.charAt(0) }}</span>
								</div>
								<div>
									<p class="font-semibold text-primary">{{ testimonial.name }}</p>
									<p class="text-xs text-charcoal/50">{{ testimonial.role }}</p>
								</div>
							</div>
						</div>
					}

					@for (testimonial of column1; track testimonial.name + '_duplicate') {
						<div class="testimonial-item relative bg-white/80 backdrop-blur-lg rounded-xl p-5 border border-white/20 shadow-lg hover:shadow-xl transition-shadow duration-300">
							<div class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
								<svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
									<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
									<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
									<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
									<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
								</svg>
							</div>
							<div class="flex gap-1 mb-3">
								@for (star of [1,2,3,4,5]; track star) {
									<svg class="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
									</svg>
								}
							</div>
							<p class="text-charcoal/80 text-base leading-relaxed mb-4">{{ testimonial.content }}</p>
							<div class="flex items-center gap-3">
								<div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
									<span class="text-primary font-semibold">{{ testimonial.name.charAt(0) }}{{ testimonial.name.split(' ')[1]?.charAt(0) }}</span>
								</div>
								<div>
									<p class="font-semibold text-primary">{{ testimonial.name }}</p>
									<p class="text-xs text-charcoal/50">{{ testimonial.role }}</p>
								</div>
							</div>
						</div>
					}
				</div>
			</div>

			<div class="testimonial-column reverse">
				<div class="testimonial-track">
					@for (testimonial of column2; track testimonial.name) {
						<div class="testimonial-item relative bg-white/80 backdrop-blur-lg rounded-xl p-5 border border-white/20 shadow-lg hover:shadow-xl transition-shadow duration-300">
							<div class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
								<svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
									<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
									<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
									<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
									<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
								</svg>
							</div>
							<div class="flex gap-1 mb-3">
								@for (star of [1,2,3,4,5]; track star) {
									<svg class="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
									</svg>
								}
							</div>
							<p class="text-charcoal/80 text-base leading-relaxed mb-4">{{ testimonial.content }}</p>
							<div class="flex items-center gap-3">
								<div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
									<span class="text-primary font-semibold">{{ testimonial.name.charAt(0) }}{{ testimonial.name.split(' ')[1]?.charAt(0) }}</span>
								</div>
								<div>
									<p class="font-semibold text-primary">{{ testimonial.name }}</p>
									<p class="text-xs text-charcoal/50">{{ testimonial.role }}</p>
								</div>
							</div>
						</div>
					}

					@for (testimonial of column2; track testimonial.name + '_duplicate') {
						<div class="testimonial-item relative bg-white/80 backdrop-blur-lg rounded-xl p-5 border border-white/20 shadow-lg hover:shadow-xl transition-shadow duration-300">
							<div class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
								<svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
									<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
									<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
									<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
									<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
								</svg>
							</div>
							<div class="flex gap-1 mb-3">
								@for (star of [1,2,3,4,5]; track star) {
									<svg class="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
									</svg>
								}
							</div>
							<p class="text-charcoal/80 text-base leading-relaxed mb-4">{{ testimonial.content }}</p>
							<div class="flex items-center gap-3">
								<div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
									<span class="text-primary font-semibold">{{ testimonial.name.charAt(0) }}{{ testimonial.name.split(' ')[1]?.charAt(0) }}</span>
								</div>
								<div>
									<p class="font-semibold text-primary">{{ testimonial.name }}</p>
									<p class="text-xs text-charcoal/50">{{ testimonial.role }}</p>
								</div>
							</div>
						</div>
					}
				</div>
			</div>

			<div class="testimonial-column">
				<div class="testimonial-track">
					@for (testimonial of column3; track testimonial.name) {
						<div class="testimonial-item relative bg-white/80 backdrop-blur-lg rounded-xl p-5 border border-white/20 shadow-lg hover:shadow-xl transition-shadow duration-300">
							<div class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
								<svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
									<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
									<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
									<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
									<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
								</svg>
							</div>
							<div class="flex gap-1 mb-3">
								@for (star of [1,2,3,4,5]; track star) {
									<svg class="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
									</svg>
								}
							</div>
							<p class="text-charcoal/80 text-base leading-relaxed mb-4">{{ testimonial.content }}</p>
							<div class="flex items-center gap-3">
								<div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
									<span class="text-primary font-semibold">{{ testimonial.name.charAt(0) }}{{ testimonial.name.split(' ')[1]?.charAt(0) }}</span>
								</div>
								<div>
									<p class="font-semibold text-primary">{{ testimonial.name }}</p>
									<p class="text-xs text-charcoal/50">{{ testimonial.role }}</p>
								</div>
							</div>
						</div>
					}

					@for (testimonial of column3; track testimonial.name + '_duplicate') {
						<div class="testimonial-item relative bg-white/80 backdrop-blur-lg rounded-xl p-5 border border-white/20 shadow-lg hover:shadow-xl transition-shadow duration-300">
							<div class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
								<svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
									<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
									<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
									<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
									<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
								</svg>
							</div>
							<div class="flex gap-1 mb-3">
								@for (star of [1,2,3,4,5]; track star) {
									<svg class="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
									</svg>
								}
							</div>
							<p class="text-charcoal/80 text-base leading-relaxed mb-4">{{ testimonial.content }}</p>
							<div class="flex items-center gap-3">
								<div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
									<span class="text-primary font-semibold">{{ testimonial.name.charAt(0) }}{{ testimonial.name.split(' ')[1]?.charAt(0) }}</span>
								</div>
								<div>
									<p class="font-semibold text-primary">{{ testimonial.name }}</p>
									<p class="text-xs text-charcoal/50">{{ testimonial.role }}</p>
								</div>
							</div>
						</div>
					}
				</div>
			</div>

		</div>
	</div>
</section> -->

<!-- Contact & Location Section -->
<section class="relative py-20 md:py-28 lg:py-32 bg-gradient-to-br from-mist via-white to-mist overflow-hidden">
	<!-- Decorative Background Elements -->
	<div class="absolute inset-0 opacity-5">
		<div class="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full blur-3xl"></div>
		<div class="absolute bottom-20 right-10 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
	</div>

	<div class="relative max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
		<!-- Section Header -->
		<div class="text-center mb-12 md:mb-16" id="map-div">
			<div class="inline-flex items-center gap-3 mb-4">
				<div class="h-px w-12 bg-gradient-to-r from-transparent to-secondary"></div>
				<span class="text-secondary text-sm font-semibold tracking-widest uppercase">{{ contactTagline() }}</span>
				<div class="h-px w-12 bg-gradient-to-l from-transparent to-secondary"></div>
			</div>
			<h2 class="font-cenzo text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4 tracking-tight">
				{{ contactTitle() }}
			</h2>
			<p class="text-charcoal/60 text-sm md:text-base max-w-2xl mx-auto">
				{{ contactDescription() }}
			</p>
		</div>

		<!-- Contact Grid -->
		<div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
			<!-- Left: Contact Information -->
			<div class="space-y-6">
				<!-- Contact Card with Glass Effect -->
				<div class="bg-white/80 backdrop-blur-lg rounded-2xl p-8 md:p-10 shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-500">
					<!-- Phone -->
					<div class="mb-8">
						<div class="flex items-start gap-4 group">
							<div class="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
								<svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
								</svg>
							</div>
							<div>
								<h3 class="text-sm font-semibold text-charcoal/50 mb-2 uppercase tracking-wider">Call Us</h3>
								<a [href]="'tel:' + contactPhone()" class="text-xl md:text-2xl font-bold text-primary hover:text-secondary transition-colors duration-300">
									{{ contactPhone() }}
								</a>
								<p class="text-sm text-charcoal/60 mt-1">{{ contactHours() }}</p>
							</div>
						</div>
					</div>

					<!-- Address -->
					<div class="mb-8">
						<div class="flex items-start gap-4 group">
							<div class="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-secondary to-secondary/80 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
								<svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
								</svg>
							</div>
							<div>
								<h3 class="text-sm font-semibold text-charcoal/50 mb-2 uppercase tracking-wider">Our Location</h3>
								<p class="text-lg font-semibold text-primary leading-relaxed">
									{{ contactAddressLine1() }}<br/>
									{{ contactAddressLine2() }}
								</p>
							</div>
						</div>
					</div>

					<!-- Email -->
					<div>
						<div class="flex items-start gap-4 group">
							<div class="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-primary/80 to-secondary flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
								<svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
								</svg>
							</div>
							<div>
								<h3 class="text-sm font-semibold text-charcoal/50 mb-2 uppercase tracking-wider">Email Us</h3>
								<a [href]="'mailto:' + contactEmail()" class="text-xl font-bold text-primary hover:text-secondary transition-colors duration-300">
									{{ contactEmail() }}
								</a>
							</div>
						</div>
					</div>
				</div>

				<!-- CTA Button -->
				<button (click)="openBookingModal()" class="group w-full btn-slide-border relative overflow-hidden bg-gradient-to-r from-primary to-secondary text-white px-8 py-5 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
					<span class="relative z-10 flex items-center justify-center gap-3">
						Book Your Consultation
					</span>
				</button>
			</div>

			<!-- Right: Map -->
			<div class="relative group">
				<div class="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
				<div class="relative bg-white/80 backdrop-blur-lg rounded-2xl p-2 shadow-xl border border-white/20 overflow-hidden map-container">
					<div class="rounded-xl overflow-hidden">
						<iframe
							[src]="contactMapEmbed()"
							width="100%"
							height="500"
							style="border:0;"
							allowfullscreen=""
							loading="lazy"
							referrerpolicy="no-referrer-when-downgrade"
							class="grayscale hover:grayscale-0 transition-all duration-700">
						</iframe>
					</div>
					<!-- Location Badge Overlay -->
					<div class="absolute bottom-8 right-8 bg-white/95 backdrop-blur-md rounded-full px-5 py-3 shadow-lg border border-primary/10">
						<div class="flex items-center gap-2">
							<div class="w-3 h-3 bg-primary rounded-full animate-pulse"></div>
							<span class="text-sm font-semibold text-primary">We're Here</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</section>

<!-- Booking Modal -->
@if (showBookingModal()) {
	<app-booking-modal (close)="closeBookingModal()"></app-booking-modal>
}
